import { VStack } from "@chakra-ui/layout"
import React, { useMemo } from "react"
import { Control, Controller } from "react-hook-form"
import styled from "styled-components"

import { GetAuthRegister } from "~/api/auth/register/get"
import { useSWRForGetMasterSchools } from "~/api/master/schools/get"
import Input from "~/components/atoms/Input"
import Selectbox from "~/components/atoms/Selectbox"
import { DoubleColumnsGrid } from "~/components/layouts/DoubleColumnsGrid"
import { FormContainer } from "~/components/molecules/FormContainer"
import { InputAutoSuggest } from "~/components/molecules/InputAutoSuggest"
import { Color, LineHeight, SpacingSize, TextSize } from "~/consts/style-mixins"
import { useComputedIfNotNull } from "~/hooks/useComputedIfNotNull"
import { createOptions, Option } from "~/services/createOptions"

import { Section } from "../components/Section"
import { LABELS, VALIDATE_MAP } from "../constants"
import { HackedPostAuthRegisterBody } from "../types"
import { useAuthRegisterRouterQuery } from "../useAuthRegisterRouterQuery"

type Props = {
  control: Control<HackedPostAuthRegisterBody>
  formMasterData: GetAuthRegister
}

export const BasicInfo: React.VFC<Props> = ({ control, formMasterData }) => {
  const {
    register,
    _formState: { errors },
  } = control

  const { email: defaultEmail } = useAuthRegisterRouterQuery()
  const { data: masterSchoolsJson } = useSWRForGetMasterSchools()

  const { masterSchools } = useComputedIfNotNull(masterSchoolsJson, ({ data }) => {
    return { masterSchools: data }
  })

  const options = useMemo(() => {
    const graduationYears = formMasterData.graduation_years || []
    const categories = formMasterData.categories || []
    const schoolGrades = formMasterData.school_grades || []

    let graduatesInOptions = graduationYears.map((y): Option => ({ text: `${y}年卒`, value: `${y}` }))
    return {
      facultyCategory: createOptions(categories, { isAddInitialOption: true }),
      year: createOptions(schoolGrades, { isAddInitialOption: true }),
      graduatesIn: createOptions(graduatesInOptions, { isAddInitialOption: true }),
    }
  }, [formMasterData])

  return (
    <Section title="基本情報">
      <VStack spacing={SpacingSize._2XL}>
        <FormContainer isRequired label={LABELS.email} errorMessage={errors.email?.message}>
          <Input
            {...register("email", { value: defaultEmail, validate: VALIDATE_MAP.email })}
            type="email"
            placeholder="example@gmail.com"
            autoComplete="email"
            isError={!!errors.email}
          />
        </FormContainer>

        <FormContainer isRequired label={LABELS.password} errorMessage={errors.password?.message}>
          <Input
            {...register("password", { validate: VALIDATE_MAP.password })}
            type="password"
            placeholder="●●●●●●●●"
            autoComplete="new-password"
            isError={!!errors.password}
          />
        </FormContainer>

        <FormContainer isRequired label={LABELS.name} errorMessage={errors.name?.message}>
          <Input
            {...register("name", { validate: VALIDATE_MAP.name })}
            placeholder="山田 太郎"
            autoComplete="name"
            isError={!!errors.name}
          />
        </FormContainer>

        <FormContainer isRequired label={LABELS.userSchool} errorMessage={errors.user_school?.message}>
          <Controller
            name="user_school"
            control={control}
            rules={{ validate: VALIDATE_MAP.user_school }}
            render={({ field: { onChange }, fieldState: { error } }) => (
              <InputAutoSuggest
                placeholder="慶應義塾大学"
                suggestions={masterSchools ?? []}
                onChange={onChange}
                error={!!error}
              />
            )}
          />
        </FormContainer>

        <DoubleColumnsGrid>
          <FormContainer isRequired label={LABELS.userFaculty} errorMessage={errors.user_faculty?.message}>
            <Input
              {...register("user_faculty", { validate: VALIDATE_MAP.user_faculty })}
              placeholder="経営学部経営学科"
              isError={!!errors.user_faculty}
            />
          </FormContainer>

          <FormContainer isRequired label={LABELS.facultyCategoryId} errorMessage={errors.faculty_category_id?.message}>
            <Selectbox
              options={options.facultyCategory ?? []}
              selectProps={register("faculty_category_id", {
                validate: VALIDATE_MAP.faculty_category_id,
                valueAsNumber: true,
              })}
              isError={!!errors.faculty_category_id}
            />
          </FormContainer>
        </DoubleColumnsGrid>

        <FormContainer isRequired label={LABELS.year} errorMessage={errors.year?.message}>
          <Selectbox
            options={options.year ?? []}
            selectProps={register("year", { validate: VALIDATE_MAP.year })}
            isError={!!errors.year}
          />
          {new Date().getMonth() === 2 && <YearNote>※4月1日に自動で1学年上に繰り上がります</YearNote>}
        </FormContainer>

        {/* <FormContainer isOptional label={LABELS.introductionKey} errorMessage={errors.introduction_key?.message}>
          <Input
            {...register("introduction_key", { validate: VALIDATE_MAP.introduction_key })}
            placeholder="xxxxxxx"
            isError={!!errors.introduction_key}
          />
        </FormContainer> */}
      </VStack>
    </Section>
  )
}

export const YearNote = styled.p`
  margin-top: 6px;
  font-size: ${TextSize.XS}px;
  line-height: ${LineHeight.XS}%;
  color: ${Color.Gray}; ;
`
